import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../components/ui';

/**
 * Asks before leaving a dirty form: reload and tab close (browser prompt), and any in-app link or `leave()` call.
 * The app uses BrowserRouter, which has no navigation blocker, so the Back button is not intercepted.
 */
export const useUnsavedGuard = (dirty: boolean) => {
    const { t } = useTranslation('menu');
    const navigate = useNavigate();
    const location = useLocation();
    const bypass = useRef(false);
    const [pending, setPending] = useState<string | null>(null);
    const here = location.pathname + location.search + location.hash;

    useEffect(() => {
        if (!dirty) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            if (!bypass.current) e.preventDefault();
        };
        const onClick = (e: MouseEvent) => {
            if (bypass.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            const anchor = e.target instanceof Element ? e.target.closest('a[href]') : null;
            if (!(anchor instanceof HTMLAnchorElement) || anchor.target === '_blank' || anchor.origin !== window.location.origin) return;
            const to = anchor.pathname + anchor.search + anchor.hash;
            if (to === here) return;
            e.preventDefault();
            e.stopPropagation();
            setPending(to);
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        document.addEventListener('click', onClick, true);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            document.removeEventListener('click', onClick, true);
        };
    }, [dirty, here]);

    return {
        /** Call right before navigating away after a successful save. */
        allowLeaving: () => {
            bypass.current = true;
        },
        /** Navigate, asking first when there are unsaved changes. */
        leave: (to: string) => (dirty && !bypass.current ? setPending(to) : navigate(to)),
        dialog: (
            <ConfirmDialog
                open={pending !== null}
                onClose={() => setPending(null)}
                onConfirm={() => {
                    bypass.current = true;
                    if (pending) navigate(pending);
                    setPending(null);
                }}
                title={t('guard.title')}
                confirmLabel={t('guard.leave')}
                cancelLabel={t('guard.stay')}
            >
                {t('guard.body')}
            </ConfirmDialog>
        ),
    };
};
